import { useCallback, useMemo } from 'react';
import { BooleanFilter } from 'utils/TypeUtils';
import { useDCSBundlesQuery } from './DCS/useDCSBundlesQuery';
import { useDCSProductsQuery } from './DCS/useDCSProductsQuery';
import { useDCSSoftwarePlansQuery } from './DCS/useDCSSoftwarePlansQuery';
import {
  mapDCSBundlesToPricings,
  mapDCSProductsToPricings,
  mapProductsWithSoftwarePlanToCartPricing,
} from './useDCS.utils';
import { IDcsPricingQuery, IProductsWithSoftwarePlan } from './useDCSTypes';

/**
 * Hook which queries for DCS pricing information, and returns filtered sets of pricings
 * based on the provided query parameters.
 * i.e. bundleIds, productIds, or productsWithSoftwarePlan
 */
export function useDCS({ bundleIds = [], productIds = [], productsWithSoftwarePlan = [] }: IDcsPricingQuery) {
  const { data: dcsBundles, error: dcsBundlesError } = useDCSBundlesQuery();
  const { data: dcsProducts, error: dcsProductsError } = useDCSProductsQuery();
  const { data: dcsSoftwarePlans, error: dcsSoftwarePlansError } = useDCSSoftwarePlansQuery();

  const productsPricings = mapDCSProductsToPricings(dcsProducts || []);
  const bundlesPricings = mapDCSBundlesToPricings(dcsBundles || []);

  const getFilteredProductPricings = useCallback(
    (ids: string[]) => {
      return productsPricings.filter(pricing => ids.includes(pricing.id));
    },
    [productsPricings],
  );

  const filteredProductPricings = useMemo(() => {
    return getFilteredProductPricings(productIds);
  }, [getFilteredProductPricings, productIds]);

  const getBundleCartPricingById = useCallback(
    (bundleId: string) => {
      return bundlesPricings.find(pricing => pricing.id === bundleId);
    },
    [bundlesPricings],
  );

  const filteredBundleCartPricings = useMemo(() => {
    return bundlesPricings.filter(pricing => bundleIds.includes(pricing.id));
  }, [bundleIds, bundlesPricings]);

  const getCustomCartPricing = useCallback(
    (customCartProducts: IProductsWithSoftwarePlan) => {
      if (!dcsSoftwarePlans || !dcsProducts) return;

      return mapProductsWithSoftwarePlanToCartPricing(customCartProducts, dcsSoftwarePlans, dcsProducts);
    },
    [dcsProducts, dcsSoftwarePlans],
  );

  const filteredCustomCartPricings = useMemo(() => {
    if (!dcsSoftwarePlans || !dcsProducts) return [];

    return productsWithSoftwarePlan.map(getCustomCartPricing).filter(BooleanFilter);
  }, [dcsProducts, dcsSoftwarePlans, getCustomCartPricing, productsWithSoftwarePlan]);

  return {
    bundlesPricings: filteredBundleCartPricings,
    productsPricings: filteredProductPricings,
    productsWithSoftwarePlanPricings: filteredCustomCartPricings,
    getFilteredProductPricings,
    getBundleCartPricingById,
    getCustomCartPricing,
    hasError: !!dcsBundlesError || !!dcsProductsError || !!dcsSoftwarePlansError,
  };
}
